import * as React from 'react';
import styled from 'styled-components/macro';
import { Modal, NewTextInput, DefaultLabel } from 'lib/components';

import { MdSave } from 'react-icons/md';
import { useQueryGroups } from 'lib/api/group/useQueryGroups';
import { Button } from 'react-covideo-common';
import { useIsMutating } from 'react-query';
import { IoMdClose } from 'react-icons/io';
import { useTranslation } from 'react-i18next';

const ModalItem = styled.div`
  box-sizing: border-box;
  width: 600px;
  padding: 32px;
  min-height: 208px;
  background-color: ${({ theme }) => theme.colors.white[100]};
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  height: 24px;
  font-size: 18px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: normal;
  color: ${({ theme }) => theme.colors.black[100]};
  align-items: center;
  justify-content: space-between;
  margin: 0;
  padding: 0;
  color: ${({ theme }) => theme.colors.black[100]};
`;

const CloseButtonWrapper = styled.label`
  display: flex;
  align-items: center;
  margin: 0;
  padding: 0;
  color: ${({ theme }) => theme.colors.neutral[60]};
  &:hover {
    cursor: pointer;
  }
`;

const Content = styled.div`
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.43;
  letter-spacing: normal;
  color: ${({ theme }) => theme.colors.black[100]};
  margin-top: 16px;
`;

const ButtonsWrapper = styled.section`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 32px;
`;

const Text = styled.div`
  color: ${({ theme }) => theme.colors.danger[100]};
`;

type Props = {
  disabled?: boolean;
  title?: string;
  text?: any;
  handleModalClose: () => void;
  handleSubmit: (data: { name: string }) => void;
};

export const ModalMergeGroup = ({ handleModalClose, handleSubmit }: Props) => {
  const { t } = useTranslation();
  const isMutating = useIsMutating();
  const isDisabled = isMutating > 0;
  const [group, setGroup] = React.useState({
    name: '',
  });
  const [error, setError] = React.useState('');

  const { data: groups } = useQueryGroups({ search: group.name, size: 10 });

  const checkIfExists = () => {
    let err = '';
    if (
      groups?.items.find(g => g.name.toLowerCase() == group.name.toLowerCase())
    ) {
      err = t('Group {{name}} already exists', { name: group.name });
    }
    setError(err);
  };

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    if (!group.name) {
      return;
    }
    checkIfExists();
  }, [groups?.items]);

  const validateName = () => {
    let err = '';
    if (!group.name) {
      err = t("Group name can't be empty");
    } else if (group.name.length > 60) {
      err = t('Group name can not contain more than 60 characters.');
    }
    if (
      groups?.items.find(g => g.name.toLowerCase() == group.name.toLowerCase())
    ) {
      err = t('Group {{name}} already exists', { name: group.name });
    }
    /*** SUS-908 changes ***/
    if (group.name && group.name.length > 60) {
      err = t('Group name can not contain more than 60 characters.');
    }
    setError(err);
  };

  return (
    <Modal>
      <ModalItem>
        <Header>
          {t('Merge groups')}
          <CloseButtonWrapper>
            <IoMdClose size={24} onClick={handleModalClose} />
          </CloseButtonWrapper>
        </Header>
        <Content>
          <DefaultLabel>{t('Merge Group Name')}</DefaultLabel>
          <NewTextInput
            style={{ marginTop: 8 }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setGroup({ ...group, name: e.target.value })
            }
            onBlur={validateName}
            placeholder={t('Name')}
          />
          {error && <Text>{error}</Text>}

          <ButtonsWrapper>
            <Button
              variant='secondary'
              text={t('Cancel')}
              onClick={handleModalClose}
              disabled={isDisabled}
            />
            <Button
              icon={<MdSave color='white' style={{ fontSize: 24 }} />}
              text={t('Save')}
              onClick={() => handleSubmit(group)}
              disabled={!!error || isDisabled}
            />
          </ButtonsWrapper>
        </Content>
      </ModalItem>
    </Modal>
  );
};
